var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * @ComponentFor EPiServer.Core.XhtmlString
 */
import * as React from 'react';
import { connect } from 'react-redux';
import { isBrowser, Link, loadScript, unloadScript, resolveSyncIfLoadedOrThrow, addScript, elementExists, format } from '@avensia/nitro5-scope';
import { supportsComponentProp } from '../EpiProperty';
const decorators = {};
export function registerDecorator(tag, decorator) {
    if (!decorators[tag]) {
        decorators[tag] = [];
    }
    decorators[tag].push(decorator);
    return () => {
        if (decorators[tag]) {
            const index = decorators[tag].indexOf(decorator);
            if (index !== -1) {
                decorators[tag].splice(index, 1);
            }
        }
    };
}
const allowedEvents = [
    'onclick',
    'onblur',
    'onkeydown',
    'onkeyup',
    'onkeypress',
    'onfocus',
    'onmouseover',
    'onmouseout',
    'onmouseup',
    'onmousedown',
    'onscroll',
    'onchange',
    'onsubmit',
    'onload',
    'onresize',
];
function htmlEventToReactEventName(htmlEvent) {
    return htmlEvent.substr(0, 2) + htmlEvent[2].toUpperCase() + htmlEvent.substr(3);
}
function isElement(item) {
    return !!item.tag;
}
function isComponent(item) {
    return !!item.componentName;
}
function hasStringInterpolationArgs(componentProps) {
    return !!componentProps && !!componentProps.stringInterpolationArgs;
}
export function renderHtmlTag(item, index, componentProps, currentTheme) {
    if (!item) {
        return null;
    }
    if (isElement(item)) {
        const attr = Object.assign({ key: index }, item.attr);
        const relevantDecorators = (decorators['*'] || []).concat(decorators[item.tag] || []);
        for (const decorator of relevantDecorators) {
            let children;
            if (Array.isArray(item.children)) {
                children = item.children.map((child, i) => renderHtmlTag(child, i, componentProps, currentTheme));
            }
            else {
                children = item.children;
            }
            const result = decorator(item, index, attr, componentProps, children);
            if (result) {
                console.info('Decorator handled rendering for', item);
                return result;
            }
        }
        allowedEvents.forEach(allowedEvent => {
            if (attr[allowedEvent]) {
                const attrWithFn = attr;
                const stringHandler = attr[allowedEvent];
                attrWithFn[htmlEventToReactEventName(allowedEvent)] = (event, ...args) => {
                    // If CMS contains javascript errors we don't want to fail execution
                    try {
                        new Function(stringHandler).apply(event.target, [event, ...args]);
                    }
                    catch (e) {
                        window.console.error(e);
                    }
                };
                delete attr[allowedEvent];
            }
        });
        if (item.tag === 'a') {
            let children;
            if (Array.isArray(item.children)) {
                children = item.children.map((child, i) => renderHtmlTag(child, i, componentProps, currentTheme));
            }
            else {
                children = item.children;
            }
            return (React.createElement(Link, Object.assign({}, attr, { to: attr.href || '' }), children));
        }
        else if (item.tag === 'style') {
            // https://github.com/facebook/react/issues/7093
            return React.createElement(item.tag, attr, item.children[0]);
        }
        else if (item.tag === 'script') {
            if (isBrowser()) {
                return (React.createElement("noscript", { key: attr.key, ref: el => {
                        var _a;
                        const anyEl = el;
                        if (el) {
                            if (((_a = item.children) === null || _a === void 0 ? void 0 : _a.some(child => child !== '')) && !anyEl.loaded) {
                                anyEl.loaded = true;
                                try {
                                    console.info('Evaluating inline script', item.children[0]);
                                    eval(item.children[0]);
                                }
                                catch (e) {
                                    console.error(e);
                                }
                                // Add this script toupled with the domElement to a list to be able to eval it again
                                // on history changes where the element is not created, but re-rendered
                                if (!elementExists(el)) {
                                    addScript({
                                        domElement: el,
                                        firstRun: true,
                                        script: item.children[0],
                                    });
                                }
                            }
                            else if (attr.src && !anyEl.loaded) {
                                anyEl.loaded = true;
                                console.info('Loading script', attr.src);
                                loadScript(getUrl(attr.src));
                            }
                        }
                        else if (attr.src) {
                            console.info('Unloading script', attr.src);
                            unloadScript(getUrl(attr.src));
                        }
                    } }));
            }
            else {
                // We don't want to include a script tag when running on the server because that will cause the script
                // to load once again when the client dehydrates the render tree. This has the slight disadvantage that
                // scripts will load first when everything else is up and ready and not when the browser parses the initial
                // server rendered DOM, but it's much simpler this way.
                return React.createElement("noscript", { key: attr.key });
            }
        }
        else if (item.tag === 'img') {
            attr.src = getUrl(attr.src);
            return React.createElement(item.tag, attr, item.children &&
                item.children.map((childItem, j) => renderHtmlTag(childItem, j, componentProps, currentTheme)));
        }
        else {
            return React.createElement(item.tag, attr, item.children &&
                item.children.map((childItem, j) => renderHtmlTag(childItem, j, componentProps, currentTheme)));
        }
    }
    else if (isComponent(item)) {
        const Component = resolveSyncIfLoadedOrThrow(item.componentName, currentTheme);
        const model = Object.assign({ key: index }, item);
        return React.createElement(Component, model);
    }
    else {
        if (hasStringInterpolationArgs(componentProps) && componentProps.stringInterpolationArgs) {
            return format(item, componentProps.stringInterpolationArgs);
        }
        else {
            return item;
        }
    }
}
function getUrl(url) {
    if (!url) {
        return '';
    }
    if (typeof url === 'string') {
        return url;
    }
    return url.url;
}
export default supportsComponentProp(connect((state) => ({
    currentTheme: state.currentTheme,
}))((_a) => {
    var { component: Component = 'div', className = '', currentTheme } = _a, restProps = __rest(_a, ["component", "className", "currentTheme"]);
    return (React.createElement(Component, { className: className }, restProps.html && restProps.html.map((item, i) => renderHtmlTag(item, i, restProps, currentTheme))));
}));
